import { Page, SimplePagination, Table } from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import { ReactNode, useCallback, useEffect } from 'react';

import { WithPresenceInjectedProps } from 'hocs';
import { PresenceFilter } from 'hocs/presence/withPresence';

import { TeamList } from '../containers/teamList';
import styles from '../teamPage.module.scss';

import { TeamHeader } from './teamHeader/teamHeader';

export const TEAM_NUMBER_OF_COLUMNS = 1;

export type TeamPageDispatchPropsT = {
  selectTeamMembersFilter: (string: string) => void;
  presenceGetAll: (userId: string) => Promise<unknown>;
  setPresenceFilters: (payload: {
    filter?: PresenceFilter;
    isAssignedOnly?: boolean;
    searchTerm?: string;
  }) => void;
};

export type TeamPageStatePropsT = {
  favourites: Array<string>;
  presenceFilters: {
    filter?: PresenceFilter;
    isAssignedOnly?: boolean;
    searchTerm?: string;
  };
};

type TeamPagePropsT = WithPresenceInjectedProps &
  TeamPageDispatchPropsT &
  TeamPageStatePropsT;

export function TeamPage(props: TeamPagePropsT): JSX.Element {
  const { favourites, presenceFilters, presence, setPresenceFilters } = props;

  // componentDidMount logic
  useEffect(() => {
    const { filter, isAssignedOnly, searchTerm } = presenceFilters;

    if (searchTerm) {
      presence.provideSearchTerm(searchTerm);
    }

    if (typeof isAssignedOnly === 'boolean') {
      presence.provideAssignedOnly(isAssignedOnly);
    }

    if (filter) {
      presence.provideFilter(filter as PresenceFilter);
    }

    presence.queryItemsWithTotal(favourites);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // componentDidUpdate for favourites
  useEffect(() => {
    presence.provideFavourites(favourites);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [favourites]);

  const onSearch = useCallback(
    (values: { team_search: string }) => {
      presence.searchTermChange(values.team_search);
      setPresenceFilters({
        ...presenceFilters,
        searchTerm: values.team_search,
      });
    },
    [presence, setPresenceFilters, presenceFilters],
  );

  const handleOnClickFilter = useCallback(
    (key: PresenceFilter) => {
      presence.filterChange(key);
      setPresenceFilters({
        ...presenceFilters,
        filter: key,
      });
      ampli.teamFilterMenuItemClicked({ type: key });
    },
    [presence, setPresenceFilters, presenceFilters],
  );

  const onNextPageClicked = useCallback(() => {
    presence.nextPage();
    ampli.teamNextPageClicked();
  }, [presence]);

  const onPreviousPageClicked = useCallback(() => {
    presence.previousPage();
    ampli.teamPreviousPageClicked();
  }, [presence]);

  const onAssignedOnlyClicked = useCallback(() => {
    presence.assignedFilterChange();
    setPresenceFilters({
      ...presenceFilters,
      isAssignedOnly: !presence.isAssignedOnly,
    });
    ampli.teamAssignedAgentsOnlyClicked();
  }, [presence, setPresenceFilters, presenceFilters]);

  const onSearchClose = useCallback(() => {
    presence.searchTermChange('');
    setPresenceFilters({
      ...presenceFilters,
      searchTerm: '',
    });
  }, [presence, setPresenceFilters, presenceFilters]);

  const renderFoot = (): ReactNode => {
    const { hasNextPage, hasPreviousPage, itemsAsyncStatus, items } = presence;
    const hasPagination = items.length > 0 && (hasNextPage || hasPreviousPage);

    if (!hasPagination) {
      return null;
    }

    return (
      <Table.Footer>
        <SimplePagination
          previousButtonClick={onPreviousPageClicked}
          previousButtonDisabled={!hasPreviousPage}
          nextButtonClick={onNextPageClicked}
          nextButtonDisabled={!hasNextPage}
          loading={itemsAsyncStatus === 'loading'}
        />
      </Table.Footer>
    );
  };

  const { filter, isAssignedOnly, total, itemsAsyncStatus } = presence;

  return (
    <Page>
      <TeamHeader
        isAssignedOnly={isAssignedOnly}
        filter={filter}
        itemsAsyncStatus={itemsAsyncStatus}
        total={total}
        searchTerm={presenceFilters.searchTerm}
        onAssignedOnlyClicked={onAssignedOnlyClicked}
        onSearchClose={onSearchClose}
        onFilterClick={handleOnClickFilter}
        onSearch={onSearch}
      />

      <Page.Body>
        <Table
          framed={false}
          className={styles.table}
          wrapperProps={{ className: styles.tableWrapper }}
        >
          <Table.Tbody>
            <TeamList presence={presence} />
          </Table.Tbody>

          {renderFoot()}
        </Table>
      </Page.Body>
    </Page>
  );
}
